import type * as LucideIcons from 'lucide-react';
import type { SVGProps } from 'react';
import { memo } from 'react';
import { cn } from '../../common/functions/cn';
import { useLoadIcon } from '../../common/hooks/use-load-icon';

export type OdinIconName = keyof typeof LucideIcons;

/**
 * @deprecated Bakes a pixel size into every icon. Size the icon with a `size-*` class instead.
 * See MYOSH-15204.
 */
export const OdinIconSize = {
  ExtraExtraSmall: 0,
  ExtraSmall: 1,
  SemiSmall: 2,
  Small: 3,
  Medium: 4,
  Large: 5,
} as const;
/**
 * @deprecated Bakes a pixel size into every icon. Size the icon with a `size-*` class instead.
 * See MYOSH-15204.
 */
export type OdinIconSize = (typeof OdinIconSize)[keyof typeof OdinIconSize];

export interface OdinIconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'children'> {
  icon: OdinIconName;
  className?: string;
  /**
   * @deprecated Use a `size-*` class on `className` instead. Inside a `ButtonV2` omit it entirely,
   * the button sizes its own icons. See MYOSH-15204.
   */
  size?: OdinIconSize;
  /**
   * Stroke width for the icon (1-3 typical). Default is 2.
   */
  strokeWidth?: number;
  /**
   * @deprecated Add `animate-spin` to `className` instead. See MYOSH-15204.
   */
  spin?: boolean;
  /**
   * @deprecated The focus ring belongs to the focusable element, not to the icon inside it.
   * See MYOSH-15204.
   */
  showFocus?: boolean;
}

const SIZE_PIXELS: Record<OdinIconSize, number> = {
  [OdinIconSize.ExtraExtraSmall]: 10,
  [OdinIconSize.ExtraSmall]: 16,
  [OdinIconSize.SemiSmall]: 18,
  [OdinIconSize.Small]: 20,
  [OdinIconSize.Medium]: 24,
  [OdinIconSize.Large]: 32,
};

const isSizeUtility = (token: string) => /^size-./.test(token);

const OdinIcon = memo(
  ({ icon, size, className = '', strokeWidth = 2, spin = false, showFocus = true, ...props }: OdinIconProps) => {
    const Icon = useLoadIcon(icon);
    const sizedByClassName = className.split(/\s+/).some(isSizeUtility);
    const pixels = sizedByClassName ? undefined : SIZE_PIXELS[size ?? OdinIconSize.Small];
    const iconClassNames = cn(
      'shrink-0',
      className,
      spin && 'animate-spin',
      showFocus && 'focus:outline-hidden focus:ring-0'
    );

    return (
      <Icon
        size={pixels}
        width={pixels}
        height={pixels}
        strokeWidth={strokeWidth}
        className={iconClassNames}
        color="currentColor"
        {...props}
      />
    );
  }
);

OdinIcon.displayName = 'OdinIcon';
export default OdinIcon;
